/*
  rg-gallery-filter.js — Grove

  Combination-scoped product gallery. A desk has ~80 media; the gallery shows
  only the photos that match the current finish + leg colour + leg style.

  - Finish comes from the selected variant's Finish option. Leg colour + leg
    style come from the six-tile leg selector (rg-leg-selector.js), which exposes
    data-current-leg-* on its root and dispatches a bubbling `leg:change` event.
  - Each gallery item (viewer slide + thumbnail) is tagged server-side with
    data-finish / data-leg-colour / data-leg-style (snippets/rg-media-tags +
    rg-media-token). A token of "all" (filename didn't match that axis) always
    matches, so lifestyle / non-pattern shots are always shown.
  - Recomputes on PUB_SUB_EVENTS.variantChange (finish) AND on `leg:change`
    (legs). After each change the surface is re-synced: the active slide resets
    to the first visible item and the sliders' pagination is refreshed
    (resetPages ignores display:none items), so the visible subset behaves as one
    contiguous gallery with no blank slides.
  - The same visibility is mirrored to the stock product-media-modal by media id
    (defensive — in this theme the expanded/enlarge view is rg-gallery-lightbox).
  - Degrades gracefully: a full-combination match that yields nothing falls back
    to finish-only, then to all media — never empty.

  Keep the token lists in sync with snippets/rg-media-token.liquid.
*/
(function () {
  function controller(gallery) {
    var rawIndex = gallery.getAttribute('data-finish-option-index');
    var finishIndex = rawIndex == null ? -1 : parseInt(rawIndex, 10);

    var legSelector = document.querySelector('[data-rg-leg-selector]');

    function legAttr(name) {
      return (
        (legSelector && legSelector.getAttribute('data-current-leg-' + name)) ||
        gallery.getAttribute('data-current-leg-' + name) ||
        ''
      ).toLowerCase();
    }

    var curFinish = (gallery.getAttribute('data-current-finish') || '').toLowerCase();
    var curLegColour = legAttr('colour');
    var curLegStyle = legAttr('style');

    // Nothing to filter on -> leave Dawn's stock gallery untouched.
    if (!(finishIndex >= 0) && !curLegColour && !curLegStyle) return;

    var viewer = gallery.querySelector('[id^="GalleryViewer"]');

    function allTagged() {
      return Array.prototype.slice.call(gallery.querySelectorAll('[data-finish]'));
    }
    function viewerItems() {
      return viewer
        ? Array.prototype.slice.call(viewer.querySelectorAll('li[data-media-id][data-finish]'))
        : [];
    }
    function sliders() {
      return Array.prototype.slice.call(gallery.querySelectorAll('slider-component'));
    }

    function axisMatch(token, current) {
      if (!current) return true; // axis not in play
      if (token == null) return true; // untagged -> never hide
      return token === 'all' || token === current;
    }
    function fullMatch(el) {
      return (
        axisMatch(el.getAttribute('data-finish'), curFinish) &&
        axisMatch(el.getAttribute('data-leg-colour'), curLegColour) &&
        axisMatch(el.getAttribute('data-leg-style'), curLegStyle)
      );
    }
    function finishOnlyMatch(el) {
      return axisMatch(el.getAttribute('data-finish'), curFinish);
    }

    function norm(v) {
      var m = String(v).match(/(\d+)$/);
      return m ? m[1] : String(v);
    }

    // Mirror visibility to the stock Dawn modal (suppressed in this theme, but
    // kept consistent in case it is ever reached). Modal items carry the raw
    // media id; gallery items carry "<sectionId>-<mediaId>".
    function syncModal(predicate) {
      try {
        var modal = document.querySelector('product-modal, .product-media-modal');
        if (!modal) return;
        viewerItems().forEach(function (li) {
          var id = norm(li.getAttribute('data-media-id'));
          var target = modal.querySelector('[data-media-id="' + id + '"]');
          if (target) target.classList.toggle('hidden', !predicate(li));
        });
      } catch (e) {
        /* defensive: the modal is non-critical */
      }
    }

    // Pre-warm: promote the visible slides' lazy images to eager so they start
    // downloading before they're revealed (finish/leg switch, swipe). Gated by
    // `warmed` so the boot-time apply() never competes with initial render/LCP;
    // the post-load idle warm and every user-triggered change set it. Full
    // resolution only — never a low-res substitute.
    var warmed = false;
    function promoteVisible() {
      try {
        gallery.querySelectorAll('li:not(.hidden) img[loading="lazy"]').forEach(function (img) {
          img.loading = 'eager';
        });
      } catch (e) {
        /* non-fatal */
      }
    }

    function apply() {
      // Choose the tightest predicate that still leaves something visible.
      var predicate = fullMatch;
      if (viewerItems().filter(predicate).length === 0) predicate = finishOnlyMatch;
      if (viewerItems().filter(predicate).length === 0) {
        predicate = function () {
          return true;
        };
      }

      allTagged().forEach(function (el) {
        el.classList.toggle('hidden', !predicate(el));
      });

      gallery.setAttribute('data-current-finish', curFinish);
      gallery.setAttribute('data-current-leg-colour', curLegColour);
      gallery.setAttribute('data-current-leg-style', curLegStyle);

      // Make sure the active viewer slide is a visible one.
      try {
        var vItems = viewerItems();
        var active = vItems.filter(function (li) {
          return li.classList.contains('is-active') && !li.classList.contains('hidden');
        })[0];
        if (!active) {
          var firstVisible = vItems.filter(function (li) {
            return !li.classList.contains('hidden');
          })[0];
          if (firstVisible && typeof gallery.setActiveMedia === 'function') {
            gallery.setActiveMedia(firstVisible.dataset.mediaId, false);
          }
        }
      } catch (e) {
        /* leave active slide as-is */
      }

      // Re-sync slider pagination/counters to the visible subset.
      sliders().forEach(function (s) {
        try {
          if (typeof s.resetPages === 'function') s.resetPages();
        } catch (e) {
          /* ignore */
        }
      });

      syncModal(predicate);

      if (warmed) promoteVisible();
    }

    // Normalise from the server-rendered current state on load.
    apply();

    // Finish changes arrive on the shared variantChange event (published after
    // Dawn's own updateMedia, so finish filtering governs the gallery).
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, function (evt) {
        try {
          var data = evt && evt.data;
          if (!data || !data.variant) return;
          if (data.sectionId && gallery.id.indexOf(data.sectionId) === -1) return;
          if (finishIndex >= 0) {
            var opts = data.variant.options || [];
            curFinish = (opts[finishIndex] || '').toLowerCase();
          }
          warmed = true;
          apply();
        } catch (e) {
          /* non-fatal: never break the PDP over gallery filtering */
        }
      });
    }

    // Leg changes arrive on the leg selector's bubbling `leg:change` event.
    document.addEventListener('leg:change', function (evt) {
      try {
        var d = evt && evt.detail;
        if (!d) return;
        curLegStyle = (d.style || '').toLowerCase();
        curLegColour = (d.colour || '').toLowerCase();
        warmed = true;
        apply();
      } catch (e) {
        /* non-fatal */
      }
    });

    // Instant finish response: re-filter the moment the swatch is tapped instead
    // of waiting for Dawn's section fetch (variantChange) to come back — that
    // round-trip left the old finish's image on screen for ~0.5-1s. Delegated on
    // document because Dawn re-renders variant-selects after each change. The
    // later variantChange re-apply is an idempotent no-op.
    document.addEventListener('change', function (evt) {
      try {
        var inp = evt.target;
        if (!inp || inp.type !== 'radio') return;
        if (!/^(finish|colour|color)-\d+$/i.test(inp.name || '')) return;
        if (!inp.checked) return;
        curFinish = (inp.value || '').toLowerCase();
        warmed = true;
        apply();
      } catch (e) {
        /* non-fatal */
      }
    });

    // Post-load idle warm: 800ms after `load` keeps the downloads clear of LCP;
    // the current combination is only a handful of images.
    function idleWarm() {
      warmed = true;
      promoteVisible();
    }
    if (document.readyState === 'complete') {
      setTimeout(idleWarm, 800);
    } else {
      window.addEventListener('load', function () { setTimeout(idleWarm, 800); }, { once: true });
    }
  }

  function boot() {
    document
      .querySelectorAll('media-gallery[data-finish-option-index], media-gallery[data-leg-filter]')
      .forEach(controller);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
